import { useState, useEffect } from "react";
import { apiClient } from "@/App";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { useConfirmation } from "@/components/ConfirmationProvider";
import { Plus, Stethoscope, Edit2, Trash2, Phone, Mail, MapPin, User } from "lucide-react";

const SPECIALTIES = [
  "Speech Therapy",
  "Occupational Therapy",
  "Behavioral Therapy",
  "Physical Therapy",
  "ABA Therapy",
  "Educational Specialist",
  "Psychologist",
  "Psychiatrist",
  "Developmental Pediatrician",
  "Other"
];

export default function ProvidersPage() {
  const confirm = useConfirmation();
  const [providers, setProviders] = useState([]);
  const [children, setChildren] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingProvider, setEditingProvider] = useState(null);
  const [form, setForm] = useState({
    name: "",
    specialty: "",
    email: "",
    phone: "",
    address: "",
    notes: "",
    child_id: ""
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [providersRes, childrenRes] = await Promise.all([
        apiClient.get("/providers"),
        apiClient.get("/children")
      ]);
      setProviders(providersRes.data);
      setChildren(childrenRes.data);
    } catch (error) {
      toast.error("Failed to load data");
    }
    setLoading(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingProvider) {
        await apiClient.put(`/providers/${editingProvider.id}`, form);
        toast.success("Therapist updated!");
      } else {
        await apiClient.post("/providers", form);
        toast.success("Therapist added!");
      }
      fetchData();
      resetForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save");
    }
  };

  const handleDelete = async (id) => {
    if (!(await confirm({ title: "Delete therapist?", description: "This therapist will be permanently removed." }))) return;
    try {
      await apiClient.delete(`/providers/${id}`);
      toast.success("Therapist deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete");
    }
  };

  const openEdit = (provider) => {
    setEditingProvider(provider);
    setForm({
      name: provider.name,
      specialty: provider.specialty,
      email: provider.email,
      phone: provider.phone,
      address: provider.address,
      notes: provider.notes,
      child_id: provider.child_id
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setForm({ name: "", specialty: "", email: "", phone: "", address: "", notes: "", child_id: "" });
    setEditingProvider(null);
    setDialogOpen(false);
  };

  const getChildName = (childId) => {
    const child = children.find(c => c.id === childId);
    return child?.name || "Unknown";
  };

  const getSpecialtyColor = (specialty) => {
    const colors = {
      "Speech Therapy": "bg-teal-100 text-teal-700",
      "Occupational Therapy": "bg-golden-100 text-golden-500",
      "Behavioral Therapy": "bg-coral-100 text-coral-500",
      "ABA Therapy": "bg-info/10 text-info",
      "Physical Therapy": "bg-success/10 text-success",
    };
    return colors[specialty] || "bg-muted text-muted-foreground";
  };

  if (loading) {
    return (
      <div className="space-y-6 animate-pulse">
        <div className="h-10 bg-muted rounded-lg w-48" />
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1,2,3].map(i => <div key={i} className="h-56 bg-muted rounded-3xl" />)}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="providers-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold text-primary flex items-center gap-3">
            <Stethoscope className="h-8 w-8" />
            Therapists
          </h1>
          <p className="text-muted-foreground mt-1">Manage your child's therapists</p>
        </div>

        <Dialog open={dialogOpen} onOpenChange={(open) => { if (!open) resetForm(); else setDialogOpen(true); }}>
          <DialogTrigger asChild>
            <Button className="rounded-full" data-testid="add-provider-btn">
              <Plus className="mr-2 h-4 w-4" />
              Add Therapist
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-lg rounded-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl">
                {editingProvider ? "Edit Therapist" : "Add Therapist"}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label htmlFor="child">Child</Label>
                <Select value={form.child_id} onValueChange={(v) => setForm({ ...form, child_id: v })}>
                  <SelectTrigger className="rounded-xl" data-testid="provider-child-select">
                    <SelectValue placeholder="Select child" />
                  </SelectTrigger>
                  <SelectContent>
                    {children.map(child => (
                      <SelectItem key={child.id} value={child.id}>{child.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="name">Therapist Name</Label>
                <Input
                  id="name"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Dr. Jane Smith"
                  required
                  className="rounded-xl"
                  data-testid="provider-name-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="specialty">Specialty</Label>
                <Select value={form.specialty} onValueChange={(v) => setForm({ ...form, specialty: v })}>
                  <SelectTrigger className="rounded-xl" data-testid="provider-specialty-select">
                    <SelectValue placeholder="Select specialty" />
                  </SelectTrigger>
                  <SelectContent>
                    {SPECIALTIES.map(s => (
                      <SelectItem key={s} value={s}>{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    placeholder="email@clinic.com"
                    className="rounded-xl"
                    data-testid="provider-email-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone">Phone</Label>
                  <Input
                    id="phone"
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    placeholder="(555) 123-4567"
                    className="rounded-xl"
                    data-testid="provider-phone-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="address">Address</Label>
                <Input
                  id="address"
                  value={form.address}
                  onChange={(e) => setForm({ ...form, address: e.target.value })}
                  placeholder="123 Therapy Lane"
                  className="rounded-xl"
                  data-testid="provider-address-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="notes">Notes</Label>
                <Textarea
                  id="notes"
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  placeholder="Additional notes..."
                  className="rounded-xl resize-none"
                  rows={2}
                />
              </div>

              <div className="flex gap-3 pt-4">
                <Button type="button" variant="outline" onClick={resetForm} className="flex-1 rounded-full">
                  Cancel
                </Button>
                <Button type="submit" className="flex-1 rounded-full" data-testid="save-provider-btn">
                  {editingProvider ? "Update" : "Add"}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Empty State */}
      {children.length === 0 ? (
        <Card className="border-0 shadow-lg rounded-3xl">
          <CardContent className="py-16 text-center">
            <Stethoscope className="h-16 w-16 mx-auto text-muted-foreground/30 mb-4" />
            <h3 className="font-heading text-xl font-bold mb-2">Add a Child First</h3>
            <p className="text-muted-foreground">You need to create a child profile before adding therapists.</p>
          </CardContent>
        </Card>
      ) : providers.length === 0 ? (
        <Card className="border-0 shadow-lg rounded-3xl">
          <CardContent className="py-16 text-center">
            <Stethoscope className="h-16 w-16 mx-auto text-muted-foreground/30 mb-4" />
            <h3 className="font-heading text-xl font-bold mb-2">No Therapists Yet</h3>
            <p className="text-muted-foreground mb-6">Add your child's therapists</p>
            <Button onClick={() => setDialogOpen(true)} className="rounded-full">
              <Plus className="mr-2 h-4 w-4" />
              Add Therapist
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {providers.map((provider, i) => (
            <Card 
              key={provider.id} 
              className={`border-0 shadow-lg rounded-3xl card-hover animate-fadeIn stagger-${i + 1}`}
              data-testid={`provider-card-${provider.id}`}
            >
              <CardContent className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="h-14 w-14 rounded-2xl bg-golden-100 flex items-center justify-center">
                    <Stethoscope className="h-7 w-7 text-golden-500" />
                  </div>
                  <div className="flex gap-1">
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      onClick={() => openEdit(provider)}
                      className="rounded-full h-9 w-9"
                    >
                      <Edit2 className="h-4 w-4" />
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      onClick={() => handleDelete(provider.id)}
                      className="rounded-full h-9 w-9 text-destructive hover:text-destructive"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>

                <h3 className="font-heading font-bold text-xl mb-1">{provider.name}</h3>
                <Badge className={`rounded-full mb-3 ${getSpecialtyColor(provider.specialty)}`}>
                  {provider.specialty}
                </Badge>

                <div className="space-y-2 text-sm text-muted-foreground">
                  <div className="flex items-center gap-2">
                    <User className="h-4 w-4" />
                    For: {getChildName(provider.child_id)}
                  </div>
                  {provider.phone && (
                    <div className="flex items-center gap-2">
                      <Phone className="h-4 w-4" />
                      {provider.phone}
                    </div>
                  )}
                  {provider.email && (
                    <div className="flex items-center gap-2">
                      <Mail className="h-4 w-4" />
                      <span className="truncate">{provider.email}</span>
                    </div>
                  )}
                  {provider.address && (
                    <div className="flex items-center gap-2">
                      <MapPin className="h-4 w-4" />
                      <span className="truncate">{provider.address}</span>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
